import { useActions } from '../../hooks';
import ContactModal from './ContactModal';
import { useContacts } from './useContacts';
import { formatDate } from '../../utilities';
import { useAppContext } from '../../shared';
import { Dropdown, Input, Table } from '../../components';
import { Form, FormItem, TopPanel } from '../../components';
import { Icon, Slug, Button, NavLink } from '../../components';
import { addContact, updateContact, deleteContact } from '../../api';
import { useContactActions } from './useContactActions';
import type { FormType } from './useContacts';

const Contacts = () => {
  const { meta } = useAppContext();
  const {
    nodes,
    filters,
    setFilters,
    refresh,
    intializing: loading,
  } = useContacts();
  const {
    element,
    isOpened,
    updating,
    initialValues,
    handleClose,
    handleStartAdd,
    handleAdd,
    handleEdit,
    handleStartEdit,
    handleStartDelete,
  } = useActions<FormType, FormType, FormType>({
    name: 'Contact',
    nameKey: 'email',

    onAdd: addContact,
    onEdit: updateContact,
    onDelete: deleteContact,
    onRefresh: refresh,
  });

  const { startElement, openStart, wipeElement, openWipe } =
    useContactActions(refresh);

  return (
    <div className="flex flex-col gap-y-md w-full">
      {element}
      {startElement}
      {wipeElement}
      {isOpened && (
        <ContactModal
          loading={updating}
          initialValues={initialValues}
          onClose={handleClose}
          onSumbit={initialValues ? handleEdit : handleAdd}
        />
      )}
      <TopPanel>
        <Form<NonNullable<typeof filters>>
          initialValue={filters}
          onChange={setFilters}
        >
          <FormItem<NonNullable<typeof filters>>
            label=""
            name="requiresHumanInput"
          >
            <Dropdown
              placeholder="Human Input"
              grow={false}
              options={[
                { value: '', label: 'All Contacts' },
                { value: 'true', label: 'Requires Human Input' },
                { value: 'false', label: 'No Human Input Required' },
              ]}
            />
          </FormItem>
          <FormItem<NonNullable<typeof filters>> name="campaignId" label="">
            <Dropdown
              placeholder="Campaign"
              grow={false}
              options={[
                { value: '', label: 'All Campaigns' },
                ...meta.campaigns,
              ]}
            />
          </FormItem>
          <FormItem<NonNullable<typeof filters>> name="email" label="">
            <Input placeholder="Filter By Email" />
          </FormItem>
          <FormItem<NonNullable<typeof filters>> name="phone" label="">
            <Input placeholder="Filter By Phone" />
          </FormItem>
        </Form>
        <Button icon="plus" onClick={() => handleStartAdd()}>
          Add Contact
        </Button>
      </TopPanel>
      <div className="flex-1 overflow-y-auto">
        <Table
          data={nodes}
          loading={loading}
          config={{
            columns: [
              {
                header: 'Contact',
                cellClassName: 'text-small',
                getValue: ({
                  id,
                  email,
                  phone,
                  last_name,
                  first_name,
                  requires_human_input,
                }) => (
                  <div>
                    <NavLink
                      to={`/messages_history?contactId=${id}`}
                      className="flex flex-row items-center"
                    >
                      {!!requires_human_input && (
                        <Icon
                          icon="required"
                          iconStyle="danger"
                          className="mr-sm"
                          tooltip="Requires human input"
                        />
                      )}
                      <div className="truncate">
                        {[first_name || '', last_name || '']
                          .filter(Boolean)
                          .join(' ')}
                      </div>
                      <Icon
                        icon="link"
                        tooltip="View messages"
                        className="ml-sm shrink-0"
                      />
                    </NavLink>
                    <div className="text-tiny text-textLight">{email}</div>
                    <div className="text-tiny text-textLight">{phone}</div>
                  </div>
                ),
              },
              {
                header: 'Campaign',
                getValue: ({ campaign_id: id }) => {
                  const campaign = meta.campaigns.find(v => v.value === id);

                  return <Slug text={campaign?.label || id} />;
                },
              },
              {
                header: 'Description',
                cellClassName: 'text-tiny',
                getValue: ({ description }) => {
                  return description;
                },
              },
              {
                header: 'Created',
                cellClassName: 'text-tiny',
                getValue: ({ created_at }) => formatDate(created_at),
              },
              {
                header: 'Actions',
                align: 'right',
                getValue: item => (
                  <div className="flex flex-row gap-x-sm justify-end">
                    <Button
                      icon="play"
                      tooltip="Start Campaign"
                      onClick={() => openStart(item)}
                    />
                    <Button
                      icon="edit"
                      type="primary"
                      tooltip="Edit Contact"
                      onClick={() => handleStartEdit(item)}
                    />
                    <Button
                      icon="broom"
                      tooltip="Clear Contact Data"
                      onClick={() => openWipe(item)}
                    />
                    <Button
                      icon="delete"
                      type="danger"
                      tooltip="Delete Contact"
                      onClick={() => handleStartDelete(item)}
                    />
                  </div>
                ),
              },
            ],
          }}
        />
      </div>
    </div>
  );
};

export default Contacts;
